import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import {
  TrendingUp, BarChart3, Activity, Car, Target,
  CalendarRange, Percent, DollarSign,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";

function Kpi({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneMap = {
    default: "text-zinc-900",
    success: "text-green-700",
    alert: "text-red-700",
    warning: "text-amber-700",
    accent: "text-[#F5A623]",
  };
  return (
    <div className="border-r border-b border-zinc-200 -ml-px -mt-px p-6 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between mb-6">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className={`font-display font-black text-4xl tracking-tighter ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2 font-mono">{sub}</div>}
    </div>
  );
}

export default function BI() {
  const { t, fmtCurrency, fmtNumber } = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/bi/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="overline">{t("common.loading")}</div>;

  const profitColor = (v) => (v >= 0 ? "#16A34A" : "#DC2626");
  const occColor = (v) => (v >= 70 ? "#16A34A" : v >= 40 ? "#F5A623" : "#DC2626");

  const fp = data.fleet_performance;

  const forecastData = [
    { label: t("bi.actual30"), value: data.forecast.last_30d, tone: "actual" },
    { label: t("bi.actual60"), value: data.forecast.last_60d, tone: "actual" },
    { label: t("bi.actual90"), value: data.forecast.last_90d, tone: "actual" },
    { label: t("bi.projected30"), value: data.forecast.projected_30d, tone: "projected" },
    { label: t("bi.projected60"), value: data.forecast.projected_60d, tone: "projected" },
    { label: t("bi.projected90"), value: data.forecast.projected_90d, tone: "projected" },
  ];

  return (
    <div className="space-y-8" data-testid="bi-page">
      <div>
        <div className="overline">{t("bi.pageTitle")}</div>
        <p className="text-sm text-zinc-500 mt-1">{t("bi.pageSubtitle")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-zinc-200">
        <Kpi
          testid="bi-kpi-revenue"
          icon={DollarSign}
          label={t("bi.totalRevenue")}
          value={fmtCurrency(data.totals.revenue_total)}
          sub={`${t("bi.expenses")} ${fmtCurrency(data.totals.expenses_total)}`}
        />
        <Kpi
          testid="bi-kpi-profit"
          icon={TrendingUp}
          label={t("bi.totalProfit")}
          value={fmtCurrency(data.totals.profit_total)}
          sub={data.totals.profit_total >= 0 ? t("bi.healthy") : t("bi.atRisk")}
          tone={data.totals.profit_total >= 0 ? "success" : "alert"}
        />
        <Kpi
          testid="bi-kpi-occupancy"
          icon={Percent}
          label={t("bi.occupancy")}
          value={`${data.totals.occupancy_avg_pct}%`}
          sub={t("bi.last90days")}
          tone="accent"
        />
        <Kpi
          testid="bi-kpi-fleet"
          icon={Car}
          label={t("bi.fleetSize")}
          value={data.totals.fleet_size}
          sub={t("bi.activeRentals", { n: data.totals.active_rentals })}
        />
      </div>

      {/* Forecast */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline flex items-center gap-2"><CalendarRange className="w-3 h-3" /> {t("bi.forecastTitle")}</div>
            <h3 className="font-display font-bold text-xl tracking-tight">{t("bi.forecastSubtitle")}</h3>
          </div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase">
            {t("bi.avgDaily")}: {fmtCurrency(data.forecast.avg_daily)}
          </div>
        </div>
        <div className="h-64 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={forecastData}>
              <CartesianGrid stroke="#E4E4E7" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#E4E4E7" }} />
              <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip
                cursor={{ fill: "rgba(245,166,35,0.08)" }}
                contentStyle={{ background: "#0A0A0A", border: "none", color: "#fff", borderRadius: 0, fontSize: 12 }}
                formatter={(v) => [fmtCurrency(v), t("bi.amount")]}
              />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {forecastData.map((entry, i) => (
                  <Cell key={i} fill={entry.tone === "projected" ? "#F5A623" : "#0A0A0A"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-car performance table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline flex items-center gap-2"><BarChart3 className="w-3 h-3" /> {t("bi.fleetPerformance")}</div>
            <h3 className="font-display font-bold text-xl tracking-tight">{t("bi.fleetPerformanceSubtitle")}</h3>
          </div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase">{fp.length} {t("bi.vehicles")}</div>
        </div>

        {fp.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">{t("bi.noVehicles")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("bi.vehicle")}</Th>
                <Th className="text-right">{t("bi.rentals")}</Th>
                <Th className="text-right">{t("bi.revenue")}</Th>
                <Th className="text-right">{t("bi.expenses")}</Th>
                <Th className="text-right">{t("bi.profit")}</Th>
                <Th className="text-right">{t("bi.roi")}</Th>
                <Th>{t("bi.occupancyShort")}</Th>
              </tr>
            </thead>
            <tbody>
              {fp.map((c) => (
                <tr key={c.car_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`bi-car-${c.plate}`}>
                  <Td className="font-medium">
                    {c.model}
                    <span className="text-xs text-zinc-500 font-mono ml-1">({c.plate})</span>
                  </Td>
                  <Td className="text-right font-mono">{fmtNumber(c.rentals_count)}</Td>
                  <Td className="text-right font-mono">{fmtCurrency(c.revenue)}</Td>
                  <Td className="text-right font-mono text-red-700">{fmtCurrency(c.expenses)}</Td>
                  <Td className="text-right font-mono font-semibold" style={{ color: profitColor(c.profit) }}>
                    {fmtCurrency(c.profit)}
                  </Td>
                  <Td className="text-right font-mono" style={{ color: profitColor(c.profit) }}>
                    {c.roi_pct}%
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <div className="flex-1 bg-zinc-100 h-2">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${Math.min(100, c.occupancy_90d_pct)}%`, background: occColor(c.occupancy_90d_pct) }}
                        />
                      </div>
                      <div className="font-mono text-xs w-10 text-right">{c.occupancy_90d_pct}%</div>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top earners chart */}
      {fp.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="px-6 py-4 border-b border-zinc-200">
            <div className="overline flex items-center gap-2"><Target className="w-3 h-3" /> {t("bi.topEarners")}</div>
            <h3 className="font-display font-bold text-xl tracking-tight">{t("bi.profitPerCar")}</h3>
          </div>
          <div className="h-72 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={fp.slice(0, 10)} margin={{ left: 20 }}>
                <CartesianGrid stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="plate" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#E4E4E7" }} width={70} />
                <Tooltip
                  cursor={{ fill: "rgba(245,166,35,0.08)" }}
                  contentStyle={{ background: "#0A0A0A", border: "none", color: "#fff", borderRadius: 0, fontSize: 12 }}
                  formatter={(v, _, p) => [fmtCurrency(v), p?.payload?.model || "Profit"]}
                />
                <Bar dataKey="profit" radius={[0, 2, 2, 0]}>
                  {fp.slice(0, 10).map((entry, i) => (
                    <Cell key={i} fill={profitColor(entry.profit)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 overline ${className}`}>{children}</th>;
const Td = ({ children, className = "", style }) => <td className={`px-4 py-3 ${className}`} style={style}>{children}</td>;
